import { useMemo } from 'react';
import * as THREE from 'three';
import { planeBasis, faceToGeometry } from '../../core/sketch/triangulate';
import { buildTopology } from '../../core/sketch/topology';
import type { SketchSolid } from '../../core/sketch/types';
import { AXIS_COLORS, PREVIEW_COLORS, SKETCH_COLORS } from '../../themes/colors';
import type { InferenceKind, InferredAxis } from '../../core/sketch/inference';

/**
 * Sketch draw-tool previews — lightweight live feedback under the cursor:
 * rubber-band lines, shape loops, the inference dot (colored by kind) and
 * the axis line of on-axis inference (red X / green Y / blue Z, magenta for
 * the parallel-to-edge inference — the Sketch axis language).
 */

const INFERENCE_DOT_COLORS: Record<InferenceKind, string> = {
    endpoint: SKETCH_COLORS.inferenceEndpoint,
    midpoint: SKETCH_COLORS.inferenceMidpoint,
    onEdge: SKETCH_COLORS.inferenceOnFace,
    center: SKETCH_COLORS.inferenceCenter,
    onFace: SKETCH_COLORS.inferenceOnFace,
    onGround: SKETCH_COLORS.inferenceOnFace,
    onAxis: SKETCH_COLORS.inferenceOnAxis,
    guide: SKETCH_COLORS.guide,
};

/** Feedback color for an on-axis state: world axes take the shared axis
 *  palette, the lastSegment direction keeps the magenta inference color. */
export function inferenceAxisColor(axis: InferredAxis | undefined): string {
    if (axis === 'x' || axis === 'y' || axis === 'z') return AXIS_COLORS[axis];
    return SKETCH_COLORS.inferenceOnAxis;
}

function DottedLine({ points, color }: { points: THREE.Vector3[]; color: string }) {
    const line = useMemo(() => {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true });
        const obj = new THREE.Line(geometry, material);
        obj.raycast = () => null;
        obj.renderOrder = 10;
        return obj;
    }, [points, color]);
    return <primitive object={line} />;
}

export function SketchLinePreview({ from, to }: { from: THREE.Vector3; to: THREE.Vector3 }) {
    return <DottedLine points={[from, to]} color={PREVIEW_COLORS.line} />;
}

export function SketchPolyPreview({ vertices, cursor, color }: {
    vertices: THREE.Vector3[];
    cursor: THREE.Vector3 | null;
    /** Overrides the default draft color — the on-axis rubber band takes
     *  the inferred axis's color (Sketch behavior). */
    color?: string;
}) {
    const points = cursor ? [...vertices, cursor] : vertices;
    if (points.length < 2) return null;
    return <DottedLine points={points} color={color ?? PREVIEW_COLORS.line} />;
}

export function SketchLoopPreview({ loop }: { loop: THREE.Vector3[] }) {
    if (loop.length < 3) return null;
    const closed = [...loop, loop[0]];
    // Fan triangulation: a bare position buffer draws only floor(n/3)
    // triangles (a quad would render half-filled), so index the loop
    // explicitly to fill the whole region from every side.
    const fillIndices: number[] = [];
    for (let i = 1; i < loop.length - 1; i++) fillIndices.push(0, i, i + 1);
    return (
        <group>
            <DottedLine points={closed} color={PREVIEW_COLORS.line} />
            {/* Faint fill so the region being created reads as a face */}
            <mesh raycast={() => null} renderOrder={5}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        args={[new Float32Array(loop.flatMap((p) => [p.x, p.y, p.z])), 3]}
                    />
                    <bufferAttribute
                        attach="index"
                        args={[new Uint16Array(fillIndices), 1]}
                    />
                </bufferGeometry>
                <meshBasicMaterial
                    color={SKETCH_COLORS.inferenceOnFace}
                    transparent
                    opacity={0.15}
                    side={THREE.DoubleSide}
                    depthWrite={false}
                />
            </mesh>
        </group>
    );
}

export function SketchInferenceMarker({ point, kind, axis }: {
    point: THREE.Vector3;
    kind: InferenceKind;
    axis?: InferredAxis;
}) {
    const color = kind === 'onAxis' ? inferenceAxisColor(axis) : INFERENCE_DOT_COLORS[kind];
    return (
        <mesh position={point} raycast={() => null} renderOrder={11}>
            <sphereGeometry args={[0.09, 12, 12]} />
            <meshBasicMaterial color={color} depthTest={false} />
        </mesh>
    );
}

export function SketchAxisLinePreview({ line, axis }: {
    line: { a: THREE.Vector3; b: THREE.Vector3 };
    axis?: InferredAxis;
}) {
    return <DottedLine points={[line.a, line.b]} color={inferenceAxisColor(axis)} />;
}

/**
 * From-point alignment guides (Sketch linear inference): the dotted lines
 * through the reference point(s) the cursor is aligned with. Each guide
 * takes its own axis color (red X / green Y / blue Z, magenta last-segment
 * and extensions fall back to the neutral inference colors), so a
 * convergent corner shows both engaged alignments at once.
 */
export function SketchGuideLinesPreview({ lines }: {
    lines: Array<{ a: THREE.Vector3; b: THREE.Vector3; axis?: InferredAxis }>;
}) {
    if (lines.length === 0) return null;
    return (
        <group>
            {lines.map((line, i) => (
                <DottedLine
                    key={i}
                    points={[line.a, line.b]}
                    color={line.axis ? inferenceAxisColor(line.axis) : SKETCH_COLORS.guide}
                />
            ))}
        </group>
    );
}

/**
 * Push/Pull-style extrusion preview: the base contour lifted by `distance`
 * along its normal, wired to the base with translucent wall quads and
 * vertical edges — the full "solid being born" reading (Sketch's blue
 * extrusion), not a detached floating lid. Hole contours lift and wall too,
 * and the lifted top fill is ear-cut with the holes so punched openings
 * preview like the committed face. Near-zero distance degenerates into the
 * base face (already drawn by the solid layer): outline only.
 */
export function SketchPrismPreview({ baseLoop, holes = [], normal, distance, color }: {
    baseLoop: THREE.Vector3[];
    holes?: THREE.Vector3[][];
    normal: THREE.Vector3;
    distance: number;
    color?: string;
}) {
    const lineColor = color ?? PREVIEW_COLORS.line;
    // Below the render epsilon the prism collapses onto the base face.
    const extruded = Math.abs(distance) > 1e-4;

    const preview = useMemo(() => {
        if (baseLoop.length < 3) return null;
        const lift = (p: THREE.Vector3): THREE.Vector3 => p.clone().addScaledVector(normal, distance);
        const top = baseLoop.map(lift);
        const topHoles = holes.map((h) => h.map(lift));

        // One wall quad per contour segment of the outer loop and holes.
        const wallVerts: number[] = [];
        const wallIdx: number[] = [];
        const addWalls = (base: THREE.Vector3[]): void => {
            for (let i = 0; i < base.length; i++) {
                const j = (i + 1) % base.length;
                const s = wallVerts.length / 3;
                const t0 = lift(base[i]);
                const t1 = lift(base[j]);
                wallVerts.push(
                    base[i].x, base[i].y, base[i].z,
                    base[j].x, base[j].y, base[j].z,
                    t1.x, t1.y, t1.z,
                    t0.x, t0.y, t0.z,
                );
                wallIdx.push(s, s + 1, s + 2, s, s + 2, s + 3);
            }
        };
        if (extruded) {
            addWalls(baseLoop);
            holes.forEach(addWalls);
        }

        // Hole-aware top fill: project the lifted contour + holes into the
        // face plane and ear-cut them (indices reference [outer, ...holes]).
        let fillVerts: number[] | null = null;
        let fillIdx: number[] | null = null;
        if (extruded) {
            try {
                const { u, v } = planeBasis(normal);
                const origin = top[0];
                const flat = (p: THREE.Vector3): THREE.Vector2 => {
                    const d = p.clone().sub(origin);
                    return new THREE.Vector2(d.dot(u), d.dot(v));
                };
                const triangles = THREE.ShapeUtils.triangulateShape(
                    top.map(flat),
                    topHoles.filter((h) => h.length >= 3).map((h) => h.map(flat))
                );
                fillVerts = [...top, ...topHoles].flat().flatMap((p) => [p.x, p.y, p.z]);
                fillIdx = triangles.flat();
            } catch {
                // Degenerate/self-intersecting loop: the outline still previews.
            }
        }

        // Vertical connector edges (each base vertex → its lifted vertex).
        const connectors: number[] = [];
        const connect = (base: THREE.Vector3[], lifted: THREE.Vector3[]): void => {
            base.forEach((p, i) => {
                const t = lifted[i];
                connectors.push(p.x, p.y, p.z, t.x, t.y, t.z);
            });
        };
        if (extruded) {
            connect(baseLoop, top);
            holes.forEach((h, hi) => connect(h, topHoles[hi]));
        }

        return { top, topHoles, wallVerts, wallIdx, fillVerts, fillIdx, connectors };
    }, [baseLoop, holes, normal, distance, extruded]);

    if (!preview) return null;
    return (
        <group>
            {/* Side walls read the extrusion body; depth-tested so a push
                into the solid is genuinely occluded by the real faces. */}
            {preview.wallVerts.length > 0 && (
                <mesh raycast={() => null} renderOrder={5}>
                    <bufferGeometry>
                        <bufferAttribute
                            attach="attributes-position"
                            args={[new Float32Array(preview.wallVerts), 3]}
                        />
                        <bufferAttribute
                            attach="index"
                            args={[new Uint32Array(preview.wallIdx), 1]}
                        />
                    </bufferGeometry>
                    <meshBasicMaterial
                        color={SKETCH_COLORS.inferenceOnFace}
                        transparent
                        opacity={0.22}
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
            )}
            {/* Lifted top face fill (hole-aware ear-cut). */}
            {preview.fillVerts && preview.fillIdx && (
                <mesh raycast={() => null} renderOrder={5}>
                    <bufferGeometry>
                        <bufferAttribute
                            attach="attributes-position"
                            args={[new Float32Array(preview.fillVerts), 3]}
                        />
                        <bufferAttribute
                            attach="index"
                            args={[new Uint32Array(preview.fillIdx), 1]}
                        />
                    </bufferGeometry>
                    <meshBasicMaterial
                        color={SKETCH_COLORS.inferenceOnFace}
                        transparent
                        opacity={0.15}
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
            )}
            {/* Top + hole outlines (always visible, the preview line language). */}
            <DottedLine points={[...preview.top, preview.top[0]]} color={lineColor} />
            {preview.topHoles.map((h, i) => (
                <DottedLine key={i} points={[...h, h[0]]} color={lineColor} />
            ))}
            {/* Vertical connector edges base → top. */}
            {preview.connectors.length > 0 && (
                <lineSegments raycast={() => null} renderOrder={10}>
                    <bufferGeometry>
                        <bufferAttribute
                            attach="attributes-position"
                            args={[new Float32Array(preview.connectors), 3]}
                        />
                    </bufferGeometry>
                    <lineBasicMaterial color={lineColor} depthTest={false} transparent />
                </lineSegments>
            )}
        </group>
    );
}

/**
 * Ghost preview of a solid under a transform — the wireframe "beforeimage"
 * the Move / Rotate / Scale gestures draw at the predicted transform (faint
 * fill included so it reads as a body, not loose edges). With an identity
 * matrix it doubles as the picked-solid highlight of the boolean tools.
 */
export function SketchGhostSolidPreview({ solid, matrix, color, showFaces = true }: {
    solid: SketchSolid;
    matrix: THREE.Matrix4;
    color?: string;
    showFaces?: boolean;
}) {
    const ghostColor = color ?? PREVIEW_COLORS.move;

    const ghost = useMemo(() => {
        // Shared-edge topology keeps deduplicated borders (no double-drawn
        // seams between coplanar faces), curved borders keep tessellation.
        const pts: number[] = [];
        buildTopology(solid).edges.forEach((edge) => {
            const a = edge.a.clone().applyMatrix4(matrix);
            const b = edge.b.clone().applyMatrix4(matrix);
            pts.push(a.x, a.y, a.z, b.x, b.y, b.z);
        });
        const edgeGeom = pts.length > 0
            ? new THREE.BufferGeometry()
                .setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3))
            : null;
        const faceGeoms = showFaces
            ? solid.faces
                .map((f) => faceToGeometry(f))
                .filter((g): g is THREE.BufferGeometry => g !== null)
                .map((g) => g.applyMatrix4(matrix))
            : [];
        return { edgeGeom, faceGeoms };
    }, [solid, matrix, showFaces]);

    return (
        <group>
            {ghost.faceGeoms.map((g, i) => (
                <mesh key={i} geometry={g} raycast={() => null} renderOrder={5}>
                    <meshBasicMaterial
                        color={ghostColor}
                        transparent
                        opacity={0.12}
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
            ))}
            {ghost.edgeGeom && (
                <lineSegments geometry={ghost.edgeGeom} raycast={() => null} renderOrder={10}>
                    <lineBasicMaterial color={ghostColor} depthTest={false} transparent opacity={0.9} />
                </lineSegments>
            )}
        </group>
    );
}

/** Discretized circle loop (open: first point not repeated). */
export function circleLoop(center: THREE.Vector3, radius: number, normal: THREE.Vector3, segments = 24): THREE.Vector3[] {
    const { u, v } = basisOf(normal);
    const out: THREE.Vector3[] = [];
    for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        out.push(
            center.clone()
                .addScaledVector(u, Math.cos(angle) * radius)
                .addScaledVector(v, Math.sin(angle) * radius)
        );
    }
    return out;
}

/** Discretized 3-point arc (start, bulge point on the arc, end). */
export function arcLoop(start: THREE.Vector3, through: THREE.Vector3, end: THREE.Vector3, segments = 16): THREE.Vector3[] {
    // Quadratic Bezier through (start, bulge, end): a stable, robust
    // 3-point arc approximation (exact circularity is not needed for v1
    // since loops are stored as polylines anyway).
    return bezierLoop(start, through, end, segments);
}

/** Quadratic Bezier polyline (stable 3-point arc approximation). */
export function bezierLoop(p0: THREE.Vector3, p1: THREE.Vector3, p2: THREE.Vector3, segments = 16): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const a = p0.clone().lerp(p1, t);
        const b = p1.clone().lerp(p2, t);
        out.push(a.lerp(b, t));
    }
    return out;
}

function basisOf(normal: THREE.Vector3): { u: THREE.Vector3; v: THREE.Vector3 } {
    return planeBasis(normal);
}
